import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, Send, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import NewsletterPanel from "../components/readers/NewsletterPanel";

const when = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

function SiteAddress({ d, reload }) {
  const [u, setU] = useState(d.site_url);
  const save = async () => { try { await api.put("/readers/site-url", { site_url: u }); toast.success("Site address saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel p-5 flex flex-wrap items-end gap-3" data-testid="site-address-panel">
      <div className="flex-1 min-w-[240px]"><p className="eyebrow mb-1 flex items-center gap-1"><Globe size={12} />Site address used in emails</p>
        <p className="text-[11px] text-slate-500 mb-2">Links in reminder emails point here. Change it when you move to your own domain.</p>
        <Input value={u} onChange={(e) => setU(e.target.value)} placeholder="https://…" className="h-9" data-testid="site-address-input" />
        {u.includes("preview.emergentagent.com") && <p className="text-[11px] text-amber-500 mt-1" data-testid="site-address-preview-warning">This is a preview address. After you publish, change it to your live site so email links work for readers.</p>}</div>
      <Button variant="outline" className="border-slate-700" onClick={save} data-testid="site-address-save">Save</Button>
    </div>
  );
}

function Subscribers({ d, reload }) {
  const del = async (s) => { if (!window.confirm(`Remove ${s.email}?`)) return; try { await api.delete(`/readers/subscribers/${s.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel" data-testid="subscribers-panel">
      <div className="p-4 flex flex-wrap justify-between items-center gap-3 border-b border-slate-800">
        <p className="text-sm"><span className="font-display text-2xl text-amber-200 mr-2" data-testid="subscribers-active-count">{d.active_count}</span>active subscribers</p>
        <a href={`${API}/readers/subscribers.csv`} className="inline-flex items-center gap-1 text-sm text-amber-200 hover:text-white" data-testid="subscribers-csv"><Download size={14} />Download spreadsheet (CSV)</a>
      </div>
      {!d.subscribers.length && <p className="p-6 text-sm text-slate-500" data-testid="subscribers-empty">No sign-ups yet. Readers join from the newsletter box on your public showcase.</p>}
      <div className="divide-y divide-slate-800 max-h-[460px] overflow-y-auto scrollbar-thin">
        {d.subscribers.map((s) => (
          <div key={s.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`subscriber-row-${s.id}`}>
            <span className="flex-1 truncate">{s.email}{s.name && <span className="text-slate-500"> · {s.name}</span>}</span>
            <span className={`text-[11px] ${s.status === "active" ? "text-emerald-300" : "text-slate-500"}`}>{s.status}</span>
            <span className="text-[11px] text-slate-500 w-24 text-right">{when(s.created_at)}</span>
            <button onClick={() => del(s)} className="text-slate-500 hover:text-red-400" aria-label="Remove" data-testid={`subscriber-delete-${s.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function WelcomePreview() {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/readers/welcome-preview").then((r) => setW(r.data)).catch(() => {}); }, []);
  if (!w) return null;
  return (
    <div className="panel p-4 space-y-3" data-testid="welcome-preview-panel">
      <div><p className="eyebrow">Welcome email</p>
        <p className="text-sm text-slate-400 mt-1" data-testid="welcome-preview-source">Sent once, automatically, to each new subscriber. {w.book ? <>It includes the opening of <span className="text-amber-200">{w.book}</span> — your newest showcased book with a sample chapter (released books first).</> : "Add a sample chapter to a showcased book to include a free excerpt."}</p></div>
      <iframe title="Welcome email preview" srcDoc={w.html} sandbox="" className="w-full h-[420px] rounded-md border border-slate-800 bg-[#0d0a14]" data-testid="welcome-preview" />
    </div>
  );
}

function Reminders({ d, reload }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/readers/reminders/send-due"); data.error ? toast.error(data.error) : toast.success(`${data.sent} reminder email${data.sent === 1 ? "" : "s"} sent${data.failed ? `, ${data.failed} failed` : ""}`); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="reminders-panel">
      <div className="panel p-4 flex flex-wrap justify-between items-center gap-3">
        <p className="text-sm text-slate-400 max-w-xl">Readers who tapped "Remind me" on a Coming Soon book get one email automatically on release day (checked every hour).{d.last_run && <span className="block text-[11px] text-slate-500 mt-1">Last automatic check: {new Date(d.last_run.finished_at).toLocaleString()}</span>}</p>
        <Button variant="outline" className="border-slate-700" onClick={send} disabled={busy} data-testid="reminders-send-due"><Send size={13} className="mr-1" />{busy ? "Sending…" : "Send due reminders now"}</Button>
      </div>
      {!d.reminders.length && <p className="panel p-6 text-sm text-slate-500" data-testid="reminders-empty">No reminder requests yet. Set a future release date on a book's Showcase tab to show the "Remind me" box.</p>}
      {d.reminders.map((g) => (
        <div key={g.book_id} className="panel p-4" data-testid={`reminder-book-${g.book_id}`}>
          <div className="flex flex-wrap justify-between gap-2"><Link to={`/app/books/${g.book_id}?tab=showcase`} className="font-display text-lg hover:text-amber-200">{g.title}</Link>
            <p className="text-xs text-slate-400">Release {g.release_date ? when(`${g.release_date}T00:00:00`) : "not set"} · <span className="text-amber-200" data-testid={`reminder-waiting-${g.book_id}`}>{g.waiting} waiting</span> · {g.sent} sent</p></div>
          <div className="mt-3 flex flex-wrap gap-2">{g.readers.slice(0, 40).map((r) => <span key={r.email} className={`text-[11px] px-2 py-0.5 rounded-full border ${r.status === "sent" ? "border-emerald-700 text-emerald-300" : "border-slate-700 text-slate-300"}`}>{r.email}</span>)}</div>
        </div>
      ))}
    </div>
  );
}

export default function ReadersPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/readers/overview").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "owner") load(); }, [user]);
  if (user?.role !== "owner") return <div className="panel p-6 text-sm text-slate-400" data-testid="readers-owner-only">Only the Master Owner can see reader sign-ups.</div>;
  if (!d) return null;
  return (
    <div className="max-w-5xl space-y-6" data-testid="readers-page">
      <div><p className="eyebrow mb-2">Audience</p><h1 className="font-display text-4xl sm:text-5xl">Readers</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-xl">Newsletter sign-ups and release reminders from your public showcase.</p></div>
      <SiteAddress d={d} reload={load} />
      <Tabs defaultValue="list">
        <TabsList className="bg-slate-900 border border-slate-800"><TabsTrigger value="list" data-testid="readers-tab-list">Mailing list</TabsTrigger><TabsTrigger value="reminders" data-testid="readers-tab-reminders">Release reminders</TabsTrigger><TabsTrigger value="newsletter" data-testid="readers-tab-newsletter">Newsletters</TabsTrigger></TabsList>
        <TabsContent value="list" className="mt-4 space-y-4"><Subscribers d={d} reload={load} /><WelcomePreview /></TabsContent>
        <TabsContent value="reminders" className="mt-4"><Reminders d={d} reload={load} /></TabsContent>
        <TabsContent value="newsletter" className="mt-4"><NewsletterPanel ownerEmail={d.owner_email} /></TabsContent>
      </Tabs>
    </div>
  );
}
